import { useCallback, useMemo, useRef } from 'react';

import {
  Card,
  HomeDarkIcon,
  InfoIcon,
  LazyRender,
  Skeleton,
} from '@hcs/design-system';
import { useIntersectObserver } from '@hcs/hooks';
import {
  PropertyStateCard,
  usePropertyStateListing,
} from '@hcs/property-state';
import { PropertyStateFields } from '@hcs/types';
import { SCROLL_QUERY_SELECTOR } from '@hcs/webapps';

import {
  GoogleListingFilters,
  useGoogleListingsForUser,
  useListingsDashboardEmails,
} from '../../hooks';

import styles from './GoogleListingsAgent.module.css';
const LISTING_AGENT_TYPES: { [key: string]: boolean } = {
  list: true,
  colist: true,
  colist2: true,
  colist3: true,
};
const CARD_HEIGHT = 312;
const CONTENT_HEADER_HEIGHT = 390;
const LISTING_FIELDS = [
  PropertyStateFields.currentPrice,
  PropertyStateFields.currentStatus,
  PropertyStateFields.currentStatusDate,
  [
    PropertyStateFields.bedrooms,
    PropertyStateFields.bathrooms,
    PropertyStateFields.livingArea,
  ],
];

const AgentLabel = ({ entityId }: { entityId: string }) => {
  const { data: userAgentEmails } = useListingsDashboardEmails();
  const userAgentEmailSet = useMemo(
    () => new Set(userAgentEmails),
    [userAgentEmails],
  );
  const { data: propertyStateListing } = usePropertyStateListing({ entityId });
  const agentName =
    propertyStateListing?.propertyState?.listingDetailsSale?.agentOffice?.find(
      (a) =>
        LISTING_AGENT_TYPES[a?.type?.toLowerCase() || ''] &&
        a?.email &&
        userAgentEmailSet.has(a.email.toLowerCase()),
    )?.name;
  if (!agentName) return null;
  return <span className={styles.AgentLabel}>Listing agent: {agentName}</span>;
};

const NoListings = () => (
  <Card className={styles.EmptyState}>
    <div className={styles.EmptyStateIconCircle}>
      <HomeDarkIcon size={44} />
    </div>
    <h2 className={styles.EmptyStateTitle}>
      No listings on Google Search right now
    </h2>
    <p className={styles.EmptyStateBody}>
      You don&rsquo;t have any active or coming soon listings currently
      syndicated to Google Search.
    </p>
    <div className={styles.EmptyStatePill}>
      <InfoIcon className={styles.EmptyStatePillIcon} size={16} />
      New listings will automatically appear here when they are syndicated to
      Google Search.
    </div>
  </Card>
);

const SkeletonCards = () => (
  <>
    <Card className={styles.SkeletonCard}>
      <Skeleton />
    </Card>
    <Card className={styles.SkeletonCard}>
      <Skeleton />
    </Card>
    <Card className={styles.SkeletonCard}>
      <Skeleton />
    </Card>
    <Card className={styles.SkeletonCard}>
      <Skeleton />
    </Card>
  </>
);

export const GoogleListingsAgent = ({ search }: GoogleListingFilters) => {
  const {
    isLoading: isLoadingListings,
    isSuccess: isSuccessListings,
    data: listings,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGoogleListingsForUser({
    search,
  });

  // Load the next page when the sentinel scrolls into view
  const sentinelRef = useRef<HTMLDivElement>(null);
  const handleSentinelIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );
  useIntersectObserver({
    elementRef: sentinelRef,
    root:
      typeof document !== 'undefined'
        ? document.querySelector(SCROLL_QUERY_SELECTOR)
        : null,
    callback: handleSentinelIntersect,
    // Start fetching before the user reaches the very bottom
    rootMargin: '400px',
  });

  if (isSuccessListings && !listings?.length) {
    return <NoListings />;
  }
  return (
    <div className={styles.CardGrid}>
      {isLoadingListings ? (
        <SkeletonCards />
      ) : (
        <>
          {listings?.map((listing) => {
            const listingEntityId =
              listing.propertyStateCore.propertyState?.listingDetailsSale
                ?.entityId;
            if (!listingEntityId) return null;
            return (
              <LazyRender
                key={listingEntityId}
                height={CARD_HEIGHT}
                offset={CARD_HEIGHT + CONTENT_HEADER_HEIGHT}
                scroll
                resize
                scrollContainer={SCROLL_QUERY_SELECTOR}
                className={styles.LazyRender}
              >
                <PropertyStateCard
                  propertyStateCerberusInput={{
                    propertyStateType: 'Listing',
                    cerberusListingsInput: listingEntityId
                      ? { entityId: listingEntityId }
                      : undefined,
                  }}
                  fields={LISTING_FIELDS}
                  includeStatusBadgeSale
                  showBrokerageCredit={false}
                  clickable
                  button={<AgentLabel entityId={listingEntityId} />}
                />
              </LazyRender>
            );
          })}
          {isFetchingNextPage && <SkeletonCards />}
          {hasNextPage && <div ref={sentinelRef} className={styles.Sentinel} />}
        </>
      )}
    </div>
  );
};
